import {
  fetchCataloguePage,
  normalisePublicCataloguePage,
} from "./publicCatalogue";

export const HOME_CATALOGUE_SORTS = ["overall", "value", "closing_soon"];
export const HOME_CATALOGUE_CARD_LIMIT = 4;

const HOME_CACHE_KEY = "cc.public-home-catalogue.v1";
const HOME_CACHE_VERSION = 1;

const safeStorage = (storage) => {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const normaliseString = (value) => String(value ?? "").trim();

const normaliseRankings = (rankings) => {
  if (!rankings || typeof rankings !== "object" || Array.isArray(rankings)) {
    throw new Error("invalid_home_catalogue");
  }
  const clean = {};
  const snapshotIds = new Set();
  HOME_CATALOGUE_SORTS.forEach((sort) => {
    const ranking = rankings[sort];
    const page = normalisePublicCataloguePage(ranking?.page);
    snapshotIds.add(page.snapshot_id);
    clean[sort] = {
      page,
      etag: normaliseString(ranking?.etag) || null,
    };
  });
  if (snapshotIds.size !== 1) throw new Error("mixed_home_catalogue_snapshots");
  return clean;
};

const resultFromRankings = (rankings, savedAt = null) => {
  const snapshotId = rankings.overall.page.snapshot_id;
  return {
    snapshotId,
    publishedAt: rankings.overall.page.published_at
      || rankings.value.page.published_at
      || rankings.closing_soon.page.published_at
      || null,
    rankings,
    savedAt,
  };
};

export const readHomeCatalogue = (storage) => {
  const target = safeStorage(storage);
  if (!target) return null;
  try {
    const parsed = JSON.parse(target.getItem(HOME_CACHE_KEY) || "null");
    if (
      parsed?.version !== HOME_CACHE_VERSION
      || !normaliseString(parsed?.saved_at)
      || !normaliseString(parsed?.snapshot_id)
    ) return null;
    const rankings = normaliseRankings(parsed.rankings);
    const result = resultFromRankings(rankings, parsed.saved_at);
    return result.snapshotId === parsed.snapshot_id ? result : null;
  } catch {
    return null;
  }
};

export const writeHomeCatalogue = (result, storage) => {
  if (!result?.cacheable) return false;
  const target = safeStorage(storage);
  if (!target) return false;
  try {
    const rankings = normaliseRankings(result.rankings);
    const normalised = resultFromRankings(rankings);
    if (normalised.snapshotId !== result.snapshotId) return false;
    const savedAt = new Date().toISOString();
    const cacheRankings = Object.fromEntries(HOME_CATALOGUE_SORTS.map((sort) => {
      const ranking = rankings[sort];
      return [sort, {
        etag: ranking.etag,
        page: {
          ...ranking.page,
          items: ranking.page.items.slice(0, HOME_CATALOGUE_CARD_LIMIT),
          next_cursor: null,
          has_more: false,
        },
      }];
    }));
    // A single localStorage write is the client-side promotion pointer. A
    // partial or mixed ranking bundle can never replace the previous LKG.
    target.setItem(HOME_CACHE_KEY, JSON.stringify({
      version: HOME_CACHE_VERSION,
      snapshot_id: normalised.snapshotId,
      saved_at: savedAt,
      rankings: cacheRankings,
    }));
    return true;
  } catch {
    return false;
  }
};

export const fetchHomeCatalogue = async ({
  signal,
  cached = null,
  request,
  retryDelays,
} = {}) => {
  const responses = await Promise.all(HOME_CATALOGUE_SORTS.map(async (sort) => {
    const response = await fetchCataloguePage({
      filters: { sort },
      signal,
      cached: cached?.rankings?.[sort] || null,
      request,
      retryDelays,
    });
    return [sort, response];
  }));
  const rankings = normaliseRankings(Object.fromEntries(responses.map(([sort, response]) => [
    sort,
    { page: response.page, etag: response.etag },
  ])));
  const result = resultFromRankings(rankings);
  return {
    ...result,
    cacheable: responses.every(([, response]) => response.cacheable),
  };
};
